import { useMemo, useState } from "react";
import { Alert, BasicTable, Button, CheckboxInput, Icon, Toggle, Tooltip, Typography } from "@/components/atoms";
import { BulkRowError, BulkValidatedRow, ClientBookingData } from "@/redux/apis/clientBooking/types";
import { clsx } from "@/utils";
import { useBulkBooking } from "../BulkBookingContext";
import { BOOKING_FIELDS, BookingFieldKey } from "../bookingFields";
import { STICKY_EDIT_CELL, STICKY_EDIT_HEADER } from "../reportLayout";
import { BulkValidationState } from "../useBulkValidation";
import { EditRowModal, RowEdits } from "./EditRowModal";

/** Column widths keep the horizontally-scrolling table readable rather than everything squeezing. */
const COLUMN_WIDTH: Partial<Record<BookingFieldKey, string>> = {
  pickup: "min-w-[280px]",
  destination: "min-w-[280px]",
  notesToDriver: "min-w-[240px]",
  passengerEmail: "min-w-[220px]",
};

const worstError = (errors: BulkRowError[], field: string): BulkRowError | undefined =>
  errors.find((error) => error.field === field && error.level === "error") ?? errors.find((error) => error.field === field);

interface ReviewStepProps {
  validation: BulkValidationState;
  /** Row indexes ticked for import. Everything arrives ticked; unticking is how a row is skipped. */
  selected: Set<number>;
  onToggleRow: (rowIndex: number) => void;
  onToggleAll: (rowIndexes: number[]) => void;
  /** Rows dropped from the import — gone from the table, unlike an unticked one. */
  discarded: Set<number>;
  /** Opens the discard confirmation; the modal owns the actual removal. */
  onDiscard: () => void;
  /** The operator's transfer types, vehicles and extras — feeds the edit modal's pickers. */
  bookingData?: ClientBookingData;
  onEditRow: (rowIndex: number, edits: RowEdits) => void;
}

/**
 * Step 4 — "Review and Pricing". Every row the server checked, with its problems visible and its
 * fare filled in as the chunks land.
 *
 * Editing writes back to the raw cell and re-validates just that row, so a typo is a two-second fix
 * rather than a re-upload.
 */
export const ReviewStep = ({
  validation,
  selected,
  onToggleRow,
  onToggleAll,
  discarded,
  onDiscard,
  bookingData,
  onEditRow,
}: ReviewStepProps) => {
  const { dataRows, mapping } = useBulkBooking();
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [editing, setEditing] = useState<BulkValidatedRow>();

  const rows = useMemo(() => validation.validated.filter((row) => !discarded.has(row.row_index)), [validation.validated, discarded]);

  const visibleRows = useMemo(() => (errorsOnly ? rows.filter((row) => row.status === "invalid") : rows), [rows, errorsOnly]);

  // Rows the server hasn't returned yet — drawn as skeletons below the real ones.
  const pendingCount = Math.max(validation.total - rows.length - discarded.size, 0);

  const allIndexes = rows.map((row) => row.row_index);
  const allSelected = allIndexes.length > 0 && allIndexes.every((index) => selected.has(index));

  // Broken rows split by whether they're still ticked: ticked ones hold the import back, unticked
  // ones are simply being skipped. The banner says which, because "3 rows have errors" reads as a
  // problem even once the user has dealt with it.
  const brokenRows = rows.filter((row) => row.status === "invalid");
  const blockingCount = brokenRows.filter((row) => selected.has(row.row_index)).length;

  const cellValue = (rowIndex: number, field: BookingFieldKey) => {
    const column = mapping[field];
    return column === null ? "" : dataRows[rowIndex]?.[column] ?? "";
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Directly under the modal description, as on Match Columns, so the count is read before the
          table rather than after the toolbar. The Alert atom carries the colours and the bold
          per-type icon — this was hand-rolled markup until now, for no reason. */}
      {/*
       * Held back until every row has been checked.
       *
       * It counts what has come BACK, so mid-run it read "3 of 10 bookings have errors" while forty
       * rows were still queued — a figure that looks final, keeps climbing, and asks the reader to
       * "fix or untick to continue" before they can do either.
       *
       * Nothing is hidden by waiting: a broken row tints red the moment it lands, and the toolbar
       * says how far the check has got. This line is a SUMMARY, and a summary of half the file is the
       * part that misleads.
       */}
      {!validation.isValidating && brokenRows.length > 0 && (
        <Alert
          type={blockingCount > 0 ? "danger" : "warning"}
          message={
            blockingCount > 0
              ? `${blockingCount} of ${rows.length} bookings ${
                  blockingCount === 1 ? "has an error" : "have errors"
                }. Fix or untick to continue.`
              : `${brokenRows.length} ${brokenRows.length === 1 ? "booking" : "bookings"} with errors will be skipped.`
          }
        />
      )}

      {/* Kept as its own markup rather than an Alert: it carries a retry action, and Alert renders
          its message inside a Typography with nowhere for a button to sit. */}
      {validation.error && (
        <div className="flex items-center justify-between rounded-lg bg-danger-light px-[18px] py-3">
          <Typography variant="action" className="text-danger">
            {validation.error}
          </Typography>
          <Button variant="tertiary" size="sm" onClick={validation.retry}>
            Try again
          </Button>
        </div>
      )}

      <div className="flex items-center justify-end gap-4">
        {validation.isValidating && (
          <Typography variant="small" className="mr-auto text-neutral-dark-gray">
            {validation.isResolving ? "Looking up addresses…" : `Checking bookings… ${validation.completed} of ${validation.total}`}
          </Typography>
        )}
        <label className="flex items-center gap-2">
          <Toggle size="xs" checked={errorsOnly} onChange={setErrorsOnly} />
          <Typography variant="action" className="text-neutral-dark-gray">
            Show only rows with errors
          </Typography>
        </label>
        {/* 16px from the label above, via the row's gap-4. */}
        <Button variant="secondary" size="sm" disabled={selected.size === 0} onClick={onDiscard}>
          Discard Selected Rows
        </Button>
      </div>

      <div className="relative">
        {/* Sized to content so the 14 columns stay readable and the wrapper scrolls. */}
        <BasicTable className="max-h-[45vh] [&>table]:!w-max [&>table]:!table-auto">
          <BasicTable.Row>
            <BasicTable.HeaderCell className="sticky top-0 z-10 w-12">
              <CheckboxInput label="" checked={allSelected} onChange={() => onToggleAll(allIndexes)} aria-label="Select all bookings" />
            </BasicTable.HeaderCell>
            {BOOKING_FIELDS.map((field) => (
              <BasicTable.HeaderCell key={field.key} className={clsx("sticky top-0 z-10 min-w-[160px]", COLUMN_WIDTH[field.key])}>
                {field.label}
              </BasicTable.HeaderCell>
            ))}
            <BasicTable.HeaderCell className="sticky top-0 z-10 min-w-[140px]">Estimated Fare</BasicTable.HeaderCell>
            {/* The atom already gives header cells an opaque background. */}
            <BasicTable.HeaderCell className={STICKY_EDIT_HEADER} />
          </BasicTable.Row>
          <>
            {/* Rows still being checked render as skeletons, so the table has its full height from
                the start and each row fills in place instead of the list popping into existence. */}
            {visibleRows.map((row) =>
              // A row being re-checked after an edit shows as a placeholder, the same as one that
              // hasn't been checked yet — so it's obvious the new values are still being priced.
              validation.pendingRows.has(row.row_index) ? (
                <PendingRow key={row.row_index} />
              ) : (
                <ReviewRow
                  key={row.row_index}
                  row={row}
                  selected={selected.has(row.row_index)}
                  onToggle={() => onToggleRow(row.row_index)}
                  cellValue={cellValue}
                  onEdit={() => setEditing(row)}
                />
              )
            )}
            {pendingCount > 0 && !errorsOnly && Array.from({ length: pendingCount }, (_, index) => <PendingRow key={`pending-${index}`} />)}
          </>
        </BasicTable>

        {editing && (
          <EditRowModal
            open
            onClose={() => setEditing(undefined)}
            rowNumber={editing.row_index + 1}
            values={BOOKING_FIELDS.reduce(
              (acc, field) => ({ ...acc, [field.key]: cellValue(editing.row_index, field.key) }),
              {} as Record<BookingFieldKey, string>
            )}
            errors={editing.errors}
            bookingData={bookingData}
            onSave={(edits) => {
              onEditRow(editing.row_index, edits);
              setEditing(undefined);
            }}
          />
        )}

        {!validation.isValidating && visibleRows.length === 0 && pendingCount === 0 && (
          <Typography variant="paragraph" className="py-8 text-center text-neutral-dark-gray">
            {errorsOnly ? "No rows with errors." : "No bookings found in this file."}
          </Typography>
        )}
      </div>
    </div>
  );
};

interface ReviewRowProps {
  row: BulkValidatedRow;
  selected: boolean;
  onToggle: () => void;
  cellValue: (rowIndex: number, field: BookingFieldKey) => string;
  onEdit: () => void;
}

const ReviewRow = ({ row, selected, onToggle, cellValue, onEdit }: ReviewRowProps) => (
  <BasicTable.Row
    className={clsx(
      row.status === "invalid" && "bg-danger-light/30",
      // Unticked rows aren't being booked, so they recede. Keeps the import set legible without a
      // separate column saying the same thing.
      !selected && "opacity-50"
    )}
  >
    {/*
     * The red edge goes on this CELL, not the row.
     *
     * A left edge as well as the wash behind the row, so broken rows are countable at a glance
     * instead of cell by cell — but `BasicTable` collapses its borders, and a `<tr>`'s own left
     * border loses that collapse against the cells beside it. It painted on the first invalid row of
     * the table and silently nowhere else, so the marker meant something only sometimes.
     */}
    <BasicTable.Cell className={clsx("w-12", row.status === "invalid" && "border-l-4 border-l-danger")}>
      <CheckboxInput
        label=""
        checked={selected}
        // Broken rows are tickable on purpose. Ticked-and-broken is what disables Import, which
        // forces the user to fix the row or untick it — rather than us dropping it silently.
        onChange={onToggle}
        aria-label={`Select booking ${row.row_index + 1}`}
      />
    </BasicTable.Cell>

    {BOOKING_FIELDS.map((field) => {
      const error = worstError(row.errors, field.key);
      // Addresses show what Google matched — that IS the address being booked, and it's what the
      // bookings list will display. The typed text only shows when nothing matched, so the user can
      // see which input failed.
      const resolved = field.key === "pickup" ? row.resolved.pickup : field.key === "destination" ? row.resolved.destination : null;

      return (
        <ReviewCell
          key={field.key}
          error={error}
          className={COLUMN_WIDTH[field.key]}
          value={resolved ?? cellValue(row.row_index, field.key)}
        />
      );
    })}

    <BasicTable.Cell className="min-w-[140px]">
      {!row.is_pricing_available ? (
        <Typography className="text-neutral-silver-gray">—</Typography>
      ) : row.estimated_total === null ? (
        <Typography className="text-neutral-silver-gray">{row.status === "invalid" ? "—" : "…"}</Typography>
      ) : (
        <Typography className="text-neutral-black">${row.estimated_total.toFixed(2)}</Typography>
      )}
    </BasicTable.Cell>

    <BasicTable.Cell className={clsx("relative", STICKY_EDIT_CELL)}>
      {/* The row's tint, re-applied over the opaque backing so a pinned cell still reads as part of
          its row. `relative` on the cell is what this positions against — BasicTable.Cell doesn't
          set it, unlike DataTable's own rows. */}
      {row.status === "invalid" && <span aria-hidden className="pointer-events-none absolute inset-0 bg-danger-light/30" />}
      <Button variant="tertiary" size="sm" startIcon="Edit2" onClick={onEdit} className="relative">
        Edit
      </Button>
    </BasicTable.Cell>
  </BasicTable.Row>
);

/** A row placeholder while the server is still checking it. */
const PendingRow = () => (
  <BasicTable.Row>
    <BasicTable.Cell className="w-12">
      <div className="h-4 w-4 animate-pulse rounded bg-neutral-gray" />
    </BasicTable.Cell>
    {BOOKING_FIELDS.map((field) => (
      <BasicTable.Cell key={field.key} className={clsx("min-w-[160px]", COLUMN_WIDTH[field.key])}>
        <div className="h-4 w-full animate-pulse rounded bg-neutral-gray" />
      </BasicTable.Cell>
    ))}
    <BasicTable.Cell className="min-w-[140px]">
      <div className="h-4 w-16 animate-pulse rounded bg-neutral-gray" />
    </BasicTable.Cell>
    {/* Pinned like the real rows', so the column doesn't jump as skeletons resolve. */}
    <BasicTable.Cell className={STICKY_EDIT_CELL}>
      <div className="h-4 w-12 animate-pulse rounded bg-neutral-gray" />
    </BasicTable.Cell>
  </BasicTable.Row>
);

interface ReviewCellProps {
  value: string;
  error?: BulkRowError;
  className?: string;
}

/**
 * One read-only cell. Errors tint it red, warnings amber, and the message shows on hover — Jodie's
 * `Invalid Format` / `Missing Record` treatment.
 *
 * Read-only on purpose: fourteen columns scrolling both ways leaves no room for a select or an
 * address autocomplete without it being clipped by the scroll container, and none for a label or
 * error text. Editing happens in the row modal, where every field gets a proper control.
 */
const ReviewCell = ({ value, error, className }: ReviewCellProps) => {
  const cell = (
    <span className="flex min-w-0 items-center gap-2">
      {error && (
        // Circle for an error, triangle for a warning — the same shapes Alert uses, and what the
        // design shows. InfoCircle is drawn dot-up, so it needs rotating to read as "!".
        <Icon
          name={error.level === "error" ? "InfoCircle" : "Danger"}
          variant="Bold"
          size="md"
          className={clsx("shrink-0", error.level === "error" ? "rotate-180 text-danger" : "text-warning")}
        />
      )}
      <Typography className={clsx("block truncate", error?.level === "error" && "text-danger")}>{value || "-"}</Typography>
    </span>
  );

  return (
    // The fill goes on the <td> so it covers the whole cell, gutters included — it used to sit on the
    // text alone, which left the tint floating in an otherwise white cell.
    <BasicTable.Cell
      className={clsx(
        "min-w-[160px]",
        error?.level === "error" && "bg-danger-light",
        error?.level === "warning" && "bg-warning-light",
        className
      )}
    >
      {error ? <Tooltip content={error.message}>{cell}</Tooltip> : cell}
    </BasicTable.Cell>
  );
};
